import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { EstNotes } from 'src/lib/fliko/models/notes/collections.data';
import { NoteType, GenericNote } from 'src/lib/fliko/models/notes/notes.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ContextType } from 'src/app/view/user-context/services/models/context.data';
import { DatabaseFactory } from 'src/app/shared/services/database/database.factory';
import { DatabaseService } from 'src/app/shared/services/database.service';

function isOffersEntityContext(context: ContextType | null): context is ContextType.COMPANY | ContextType.ESTATE {
  return context === ContextType.COMPANY || context === ContextType.ESTATE;
}

@Injectable({ providedIn: 'root' })
export class NoteService {
  constructor(
    private dbSrv: DatabaseService,
    private ctxDS: ContextDataService
  ) {}
  /**
   * @param {NoteType} type
   * @path /note/$type/$estateId/ for existing note types
   * @path /note/offerMember/$companyIdOrEstateId/ for offer member notes
   * @key refId
   */
  private note(type: NoteType, context = this.ctxDS.peekContext()) {
    const isCompanyMemberNote = type === NoteType.OFFER_MEMBER && context === ContextType.COMPANY;
    const factory: DatabaseFactory = isCompanyMemberNote ? this.dbSrv.companyFactory : this.dbSrv.estateFactory;
    const entityKey = isCompanyMemberNote ? '$cId' : '$eId';
    return factory.getCollection<Collection<GenericNote>>(`note/${type}/${entityKey}`);
  }

  public getAllNotes$(type: NoteType): Observable<EstNotes> {
    if (type === NoteType.OFFER_MEMBER) {
      return this.ctxDS.context$.pipe(
        filter(isOffersEntityContext),
        distinctUntilChanged(),
        switchMap((context) => this.note(type, context).getObject()),
        map((obj) => obj ?? {})
      );
    }

    return this.note(type)
      .getObject()
      .pipe(map((obj) => obj ?? {}));
  }

  public getNotes$(type: NoteType, refId: string): Observable<GenericNote[]> {
    if (type === NoteType.OFFER_MEMBER) {
      return this.ctxDS.context$.pipe(
        filter(isOffersEntityContext),
        distinctUntilChanged(),
        switchMap((context) => this.note(type, context).getElement(refId)),
        map((col) => ObjHlp.flatten<GenericNote>(col, 1))
      );
    }

    return this.note(type)
      .getElement(refId)
      .pipe(map((col) => ObjHlp.flatten<GenericNote>(col, 1)));
  }

  public setNote(type: NoteType, note: GenericNote): Promise<void> {
    return this.note(type).updateElementChild(note.refId, note.id, note);
  }

  public removeNote(type: NoteType, refId: string, noteId: string): Promise<void> {
    return this.note(type).removeElementChild(refId, noteId);
  }
}
